/**
 * JourneyView (P274) -- the lifecycle-journey spine at /journey.
 *
 * The permanent, complete answer to "where does data start and stop, and
 * what feeds what": the 9-stage data lifecycle grouped into its four
 * tempos (Set up / Govern / Operate + Comply / Prove), each stage's
 * ~2-3 live-derived steps carrying a status, a count, its owner-persona,
 * what it feeds, and a deep link (or the blocker that gates it).
 *
 * Backend (/v1/journey/status) supplies the DYNAMIC signal; the STEP_META
 * catalogue below holds the STATIC teaching metadata (route + owner),
 * with titles / feeds in i18n -- the same split as the P106 onboarding
 * ladder (backend _STEPS vs frontend STEP_ROUTE + i18n).
 */
import { InfoTip } from "shared/components/Explainer";
import { Arrow } from "shared/components/Arrow";
import { useState } from "react";
import { listOf } from "shared/i18n/listOf";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { i18n } from "shared/i18n";

import { StepProgress } from "shared/components/StepProgress";

import { MarsadIcon } from "shared/components/MarsadIcon";
import type { JourneySpineStep, JourneySpineStepStatus } from "shared/api/types";
import { useJourneySpineStatus } from "shared/journey/useJourney";
import { STEP_META } from "shared/journey/stepMeta";
import { canActOn, isMyStation } from "shared/journey/authority";
import { useMe } from "shared/api/useMe";
import { canReadBoard, canRunOperations } from "shared/api/authority";
import { roleLabel } from "shared/vocab/workflowState";
import { seatHolder, useSeatHolders } from "shared/api/useSeatHolders";
import type { MeOut } from "shared/api/types";

/** Round 16 G13 -- will this page open for this reader? Mirrors the
 *  servers' gates for the routes a finished step can point at. */
function canOpen(route: string, me: MeOut | undefined): boolean {
  if (route.startsWith("/board")) return canReadBoard(me);
  if (route.startsWith("/admin")) return me?.is_admin === true;
  if (route.startsWith("/foundation/admin")) return canRunOperations(me);
  return true;
}

// Which stages belong to which tempo band, in order.
const TEMPOS: { tempo: string; stages: number[] }[] = [
  { tempo: "setup", stages: [0, 1, 2] },
  { tempo: "govern", stages: [3, 4, 5] },
  { tempo: "operate", stages: [6, 7] },
  { tempo: "prove", stages: [8] },
];

const STATUS_TONE: Record<JourneySpineStepStatus, string> = {
  done: "bg-emerald-100 text-emerald-800 border-emerald-200",
  partial: "bg-amber-100 text-amber-900 border-amber-200",
  todo: "bg-slate-100 text-slate-600 border-slate-200",
  blocked: "bg-rose-100 text-rose-800 border-rose-200",
};

/** Round 18 G23 -- the seat an owner label stands for, where it is one
 *  seat; a label covering several (an owner of a record, Compliance,
 *  operations) names no person. */
const OWNER_SEAT: Partial<Record<string, string>> = {
  cro: "cro",
  opsres: "head_of_opsres",
  reviewer: "reviewer",
  approver: "approver",
};

/** Stations two seats sign in turn. With no leg from the server, naming
 *  the CRO's holder would be wrong the moment the CRO has signed, so
 *  these keep the seat pair until the server says which leg is open. */
const DUAL_SIGNED_STEPS = new Set([
  "ibs_admission",
  "mvb_signoff",
  "impact_tolerances",
]);

function StepRow({ step }: { step: JourneySpineStep }) {
  const { t } = useTranslation();
  const me = useMe();
  const meta = STEP_META[step.key];
  const title = t(`modules.journey.step.${step.key}.title`);
  const feeds = t(`modules.journey.step.${step.key}.feeds`);
  const owner = meta
    ? meta.ownerLabelKey
      ? t(meta.ownerLabelKey)
      : t(`modules.journey.owner.${meta.owner}`)
    : "";
  /* Round 16 N1 -- where the station's seat moves leg by leg, it waits
     on whoever holds the leg open now, named in turn; and it is the
     reader's when she holds that leg or owns a function on it. */
  const legs = step.awaiting_legs ?? [];
  // Round 18 G23 -- a station with one owning seat names its holder too:
  // "Waits on CRO / business owner" named nobody.
  const ownerSeat =
    meta && !meta.ownerLabelKey && !DUAL_SIGNED_STEPS.has(step.key)
      ? OWNER_SEAT[meta.owner]
      : undefined;
  /* Round 19 G42 -- a station whose seats sign in turn read "CRO, then
     CIO" or "CRO / business owner" with no leg open: roles, no people.
     Each seat is named by its holder, or said to be empty; an owner's
     leg is each record's own owner. */
  const seatPair: string[] | null =
    legs.length > 0 || !meta
      ? null
      : meta.ownerLabelKey
        ? ["cro", "cio"]
        : DUAL_SIGNED_STEPS.has(step.key)
          ? ["cro", "business_owner"]
          : null;
  const seats = useSeatHolders(
    legs.length > 0 || ownerSeat !== undefined || seatPair !== null,
  );
  const ownerHolder = ownerSeat ? seatHolder(seats.data, ownerSeat) : undefined;
  const waitingOn = legs.length
    ? legs
        .map((r) => {
          // Round 18 G23 -- an owner's leg is the owner of each record,
          // not whoever holds the role; it names the seat.
          if (r === "business_owner" || r === "service_owner") return roleLabel(r);
          const h = seatHolder(seats.data, r);
          return h.kind === "named"
            ? `${h.name} (${roleLabel(r)})`
            : h.kind === "nobody"
              ? t("modules.journey.seatUnheld", { role: roleLabel(r) })
              : roleLabel(r);
        })
        .join(t("modules.journey.legThen"))
    : ownerSeat && ownerHolder?.kind === "named"
      ? `${ownerHolder.name} (${roleLabel(ownerSeat)})`
      : ownerSeat && ownerHolder?.kind === "nobody"
        ? t("modules.journey.seatUnheld", { role: roleLabel(ownerSeat) })
        : seatPair && seats.data
          ? seatPair
              .map((r) => {
                if (r === "business_owner") return t("modules.journey.eachOwner");
                const h = seatHolder(seats.data, r);
                return h.kind === "named"
                  ? `${h.name} (${roleLabel(r)})`
                  : h.kind === "nobody"
                    ? t("modules.journey.seatUnheld", { role: roleLabel(r) })
                    : roleLabel(r);
              })
              .join(t("modules.journey.legThen"))
          : owner;
  /* Round 18 N11 -- a station that follows its open legs, with none
     open, waits on no signature: it read "Waits on CRO, then CIO"
     after the admission with nothing waiting. */
  const nothingWaits = step.awaiting_legs != null && legs.length === 0;
  // E3: the ratio shows whenever there IS one. This used to be hidden
  // unless the step was partial, to work around a step that counted
  // every IBS with a tolerance against the ADMITTED ones only and so
  // read "12 of 3". That was the backend mixing two populations; it
  // is fixed, and hiding the ratio hid the defect rather than the
  // oddity.
  // Round 17 P1 -- the register station names its cut: listed is the
  // step, admitted is the figure home and the board count.
  const countLabel =
    step.switched_on != null
      ? /* Round 19 B31 -- the connectors station names what it counts,
           and a connector switched off without a run stays in it. */
        [
          t("modules.journey.switchedOnVerified", {
            on: step.switched_on,
            count: step.count,
          }),
          (step.switched_off_unrun ?? 0) > 0
            ? t("modules.journey.switchedOffUnrun", {
                count: step.switched_off_unrun ?? 0,
              })
            : null,
        ]
          .filter(Boolean)
          .join("; ")
      : step.judged != null
        ? /* Round 18 B20 -- derived is not judged: "6 derived, 0 judged". */
          t("modules.journey.derivedJudged", { count: step.count, judged: step.judged })
        : step.admitted != null
          ? t("modules.journey.listedAdmitted", {
              count: step.count,
              admitted: step.admitted,
            })
          : step.baseline_pct != null && step.total != null
            ? t("modules.journey.countOfPopulation.data_baselineComposed", {
                linked: step.count.toLocaleString(i18n.language),
                of: step.total.toLocaleString(i18n.language),
                pct:
                  step.total > 0
                    ? Math.round((step.count / step.total) * 1000) / 10
                    : 0,
                baseline: step.baseline_pct,
              })
            : step.total != null
              ? /* Round 18 P11 -- "7 of 11" left the 11 unlabelled; a step
               whose denominator is a population names it ("7 of 11
               listed"). */
                t(
                  i18n.exists(`modules.journey.countOfPopulation.${step.key}`)
                    ? `modules.journey.countOfPopulation.${step.key}`
                    : "modules.journey.countOf",
                  { count: step.count, total: step.total },
                )
              : String(step.count);
  // Round 16 N1 -- a station that names its open legs is acted on by
  // whoever holds one of them, and by nobody else ("Go" was still
  // offered to the CRO after her leg, and to every administrator).
  const positions = me.data?.positions ?? [];
  const realmRoles = me.data?.realm_roles ?? [];
  const holdsLeg = legs.some((l) =>
    l === "reviewer" ? positions.includes("reviewer") : realmRoles.includes(l),
  );
  const mine =
    step.awaiting_legs != null
      ? holdsLeg
      : meta
        ? canActOn(meta.owner, me.data)
        : false;
  /* Round 16 G13 -- a finished step offered "View" to everyone, and
     Evidence out's view is the board, which refuses the treasurer.
     Someone else's finished step is offered only where the page will
     open for this reader. */
  const canView = meta ? canOpen(meta.route, me.data) : false;

  return (
    <li
      data-testid={`journey-step-${step.key}`}
      data-status={step.status}
      className="flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2"
    >
      <span
        className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_TONE[step.status]}`}
      >
        {t(`modules.journey.status.${step.status}`)}
      </span>
      {/* Round 19 I21 -- in right to left the function step's text
          collapsed to a column about 21 px wide beside its chips: a
          flex-1 with no basis gives way to every shrink-0 in the row.
          It keeps a 12rem basis now, so the chips wrap below it. */}
      <span
        data-testid={`journey-step-text-${step.key}`}
        className="min-w-0 flex-1 basis-48"
      >
        <span className="block text-sm font-medium text-slate-800">
          {title}
          {meta?.termKey && <InfoTip termKey={meta.termKey} />}{" "}
          <span className="font-normal tabular-nums text-slate-400">{countLabel}</span>
        </span>
        <span className="block text-xs text-slate-500">{feeds}</span>
        {/* Round 17 N9 -- with more than one function in flight, say
            which wait is whose. */}
        {/* Round 18 N11 -- with one function too: "Waits on Rashid
            Reviewer" said nothing of which function. */}
        {(step.awaiting_by_function ?? []).length > 0 && (
          <ul
            data-testid="journey-function-waits"
            className="mt-0.5 text-xs text-slate-600"
          >
            {(step.awaiting_by_function ?? []).map((f) => {
              const h = seats.data?.holders?.[f.leg];
              const who =
                h && h.length === 1
                  ? `${h[0]} (${roleLabel(f.leg)})`
                  : roleLabel(f.leg);
              return (
                <li key={f.name}>
                  {t("modules.journey.functionWaits", { name: f.name, who })}
                </li>
              );
            })}
          </ul>
        )}
        {/* Round 20 V1 -- a challenge given by someone who then signed
            the decision is not counted, and the station says so. */}
        {(step.not_independent ?? 0) > 0 && (
          <span
            data-testid={`journey-not-independent-${step.key}`}
            className="mt-0.5 block text-xs text-rose-700"
          >
            {t("modules.journey.notIndependent", { count: step.not_independent ?? 0 })}
          </span>
        )}
        {/* Round 20 W3 -- before anyone scores: the owners the register
            names with no sign-in address. Admission and every tolerance
            stop at the owner's signature on each of them. */}
        {(step.owners_unbound_count ?? 0) > 0 && (
          <span
            data-testid={`journey-owners-unbound-${step.key}`}
            className="mt-0.5 block text-xs text-amber-800"
          >
            {t("modules.journey.ownersUnbound", {
              count: step.owners_unbound_count ?? 0,
              names: unboundNames(t, step),
            })}{" "}
            <Link
              to="/foundation/admin/ibses"
              className="text-brand-brassDark hover:underline"
            >
              {t("modules.journey.ownersUnboundCta")} <Arrow />
            </Link>
          </span>
        )}
        {/* Round 12 A7 -- the rung that needs a shell. Bank Setup names
            this wall precisely, with the command to generate a key;
            every ladder that sent somebody at the rung said nothing,
            and that was the rung still open at five o'clock. */}
        {step.needs_deployment_setting && (
          <span
            data-testid={`journey-deployment-wall-${step.key}`}
            className="mt-0.5 block text-xs text-amber-800"
          >
            {t("adminSetup.needsDeploymentSetting", {
              name: step.needs_deployment_setting,
            })}{" "}
            <Link
              to="/admin/bank-setup"
              className="text-brand-brassDark hover:underline"
            >
              {t("adminSetup.needsDeploymentSettingCta")} <Arrow />
            </Link>
          </span>
        )}
      </span>
      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
        {owner}
      </span>
      {step.status === "blocked" && step.requires ? (
        <span
          data-testid={`journey-blocked-${step.key}`}
          className="shrink-0 text-xs text-rose-700"
        >
          {t("modules.journey.blockedBy", {
            step: t(`modules.journey.step.${step.requires}.title`),
          })}
        </span>
      ) : meta && (mine || (step.status === "done" && canView)) ? (
        <Link
          to={meta.route}
          data-testid={`journey-cta-${step.key}`}
          className="shrink-0 text-xs font-medium text-brand-brassDark hover:underline"
        >
          {step.status === "done"
            ? t("modules.journey.view")
            : holdsLeg
              ? t("modules.journey.waitsOnYou")
              : t("modules.journey.go")}
        </Link>
      ) : meta && step.status !== "done" ? (
        // E3 -- do not send somebody into a refusal. Say who it is
        // waiting on instead; that is the thing they can act on.
        <span
          data-testid={`journey-waits-on-${step.key}`}
          className="shrink-0 text-xs text-slate-500"
        >
          {nothingWaits
            ? t("modules.journey.nothingWaits")
            : t("modules.journey.waitsOn", { owner: waitingOn })}
        </span>
      ) : null}
    </li>
  );
}

/** The ladder's address before it moved to Admin as "Set up". */
export const SETUP_PATH = "/admin/setup";

/** `/journey` keeps working: it lands on Set up with its query intact
 *  (the owner filter a hand-over link carries). */
export function JourneyRedirect() {
  const location = useLocation();
  return <Navigate to={`${SETUP_PATH}${location.search}${location.hash}`} replace />;
}

export function JourneyView() {
  const { t } = useTranslation();
  const me = useMe();
  const query = useJourneySpineStatus();
  const all = query.data?.steps ?? [];
  // E3 -- the Journey is the only surface that names the owner of each
  // step, and it showed all twenty to everybody. "Which of these is
  // mine?" was a question the reader had to answer for themselves.
  const [mineOnly, setMineOnly] = useState(false);
  // D1 -- a hand-over needs to be able to point at somebody ELSE's
  // stations. The admin finishing setup is handing the ladder to the
  // resilience lead, and "here is the Journey" is not a hand-over if
  // the reader has to work out which of the steps are hers.
  const [params, setParams] = useSearchParams();
  const ownerFilter = params.get("owner");
  // E2 -- the FILTER asks whose station it is, not who could perform
  // it. `canActOn` admits an administrator everywhere, so "Only my
  // steps" removed nothing for the person most likely to press it.
  const isMine = (key: string) => {
    const meta = STEP_META[key];
    // Round 16 N1 -- the server says whose a moving station is: its
    // open leg's holder, or the owner of a function on it.
    const yours = all.find((s) => s.key === key)?.yours;
    if (yours != null) return yours;
    return meta ? isMyStation(meta.owner, me.data) : false;
  };
  const inOwnerFilter = (key: string) =>
    !ownerFilter || STEP_META[key]?.owner === ownerFilter;
  const scoped = all.filter((s) => inOwnerFilter(s.key));
  const steps = mineOnly ? scoped.filter((s) => isMine(s.key)) : scoped;
  const mineCount = scoped.filter((s) => isMine(s.key)).length;
  const byStage = (stage: number) => steps.filter((s) => s.stage === stage);

  return (
    <div data-testid="journey-view" className="mx-auto max-w-3xl space-y-6 px-4 py-6">
      <header>
        <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          {t("modules.journey.rubric")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="journey"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t("modules.journey.heading")}
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-500">
          {t("modules.journey.subtitle")}
        </p>
        {/* A7 -- this is the whole ladder, and the two shorter ones are
            slices of it. None of the four referenced any other, so a
            reader meeting a second one had no way to tell whether it
            was the same work counted again or different work. */}
        <p
          data-testid="journey-slices"
          className="mt-2 flex flex-wrap gap-x-4 text-xs text-slate-500"
        >
          <span>{t("modules.journey.slices")}</span>
          <Link
            to="/getting-started"
            data-testid="journey-slice-getting-started"
            className="font-semibold text-brand-brassDark hover:underline"
          >
            {t("modules.journey.sliceGovernance")} <Arrow />
          </Link>
          {(me.data?.is_admin ?? false) && (
            <Link
              to="/admin"
              data-testid="journey-slice-admin"
              className="font-semibold text-brand-brassDark hover:underline"
            >
              {t("modules.journey.slicePlumbing")} <Arrow />
            </Link>
          )}
        </p>
        {ownerFilter && (
          <p
            data-testid="journey-owner-filter"
            className="mt-2 flex flex-wrap items-center gap-2 rounded border border-brand-brassLine bg-brand-brassTint px-2 py-1 text-xs text-ink"
          >
            <span>
              {t("modules.journey.ownerFilter", {
                owner: t(`modules.journey.owner.${ownerFilter}`, {
                  defaultValue: ownerFilter,
                }),
              })}
            </span>
            <button
              type="button"
              data-testid="journey-owner-filter-clear"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("owner");
                setParams(next, { replace: true });
              }}
              className="font-semibold text-brand-brassDark hover:underline"
            >
              {t("modules.journey.ownerFilterClear")}
            </button>
          </p>
        )}
        {query.data && (
          <p
            data-testid="journey-my-steps"
            className="mt-2 flex flex-wrap items-center gap-2 text-xs"
          >
            <button
              type="button"
              data-testid="journey-filter-toggle"
              data-mine-only={mineOnly ? "true" : "false"}
              onClick={() => setMineOnly((v) => !v)}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 font-medium text-slate-700 hover:border-brand-brass"
            >
              {mineOnly
                ? t("modules.journey.myStepsAll")
                : t("modules.journey.myStepsMine", { count: mineCount })}
            </button>
            {mineOnly && mineCount === 0 && (
              <span data-testid="journey-none-mine" className="text-slate-500">
                {t("modules.journey.myStepsNone")}
              </span>
            )}
          </p>
        )}
        {query.data && (
          <p className="mt-2 text-xs text-slate-500">
            {/* E5 -- the filter shrank the LIST and not the count, so
                "Only my steps" left a figure describing the whole bank
                above a list describing one person. The count follows
                what is on screen.

                C2 -- and it is the SAME sentence the home ribbon and
                Getting Started show, so it is rendered by the same
                component. Three copies could drift, and had. */}
            <StepProgress
              testid="journey-progress"
              done={steps.filter((s) => s.status === "done").length}
              total={steps.length}
            />
          </p>
        )}
      </header>

      {query.isLoading && (
        <p className="text-sm text-slate-400">{t("modules.journey.loading")}</p>
      )}
      {query.isError && (
        <p className="text-sm text-rose-700">{t("modules.journey.error")}</p>
      )}

      {TEMPOS.map(({ tempo, stages }) => (
        <section
          key={tempo}
          data-testid={`journey-tempo-${tempo}`}
          className="space-y-3"
        >
          <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-brassDark">
            {t(`modules.journey.tempo.${tempo}`)}
          </h2>
          {mineOnly && stages.every((stage) => byStage(stage).length === 0) && (
            <p
              data-testid={`journey-tempo-${tempo}-none-mine`}
              className="text-xs text-slate-500"
            >
              {t("modules.journey.tempoNoneMine")}
            </p>
          )}
          {stages.map((stage) => {
            const group = byStage(stage);
            if (group.length === 0) return null;
            return (
              <div key={stage} className="space-y-2">
                <h3 className="text-xs font-semibold text-slate-600">
                  {stage}. {t(`modules.journey.stage.${stage}`)}
                </h3>
                <ul className="space-y-2">
                  {group.map((s) => (
                    <StepRow key={s.key} step={s} />
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

/** Round 20 W3 -- the unbound owners, the first few by name and the
 *  rest counted. */
function unboundNames(t: TFunction, step: JourneySpineStep): string {
  const shown = step.owners_unbound ?? [];
  const more = Math.max(0, (step.owners_unbound_count ?? 0) - shown.length);
  return more > 0
    ? t("modules.journey.ownersUnboundAndMore", {
        names: shown.join(", "),
        count: more,
      })
    : listOf(shown);
}
